import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../services/data-provider/client'

/**
 * Google Places Autocomplete Hook
 * Uses backend proxy (POST .../google/places/autocomplete and GET .../google/places/details)
 * so the API key stays on the server. Same request/response shape as Places API (New).
 */

interface AutocompleteSuggestion {
  placePrediction: {
    placeId: string
    text: {
      text: string
    }
    structuredFormat: {
      mainText: {
        text: string
      }
      secondaryText: {
        text: string
      }
    }
  }
}

interface ParsedAddress {
  address?: string
  city?: string
  postalCode?: string
  province?: string
  country?: string
  latitude?: number
  longitude?: number
}

interface UseGooglePlacesAutocompleteOptions {
  onPlaceSelected?: (parsedAddress: ParsedAddress) => void
  country?: string // 2-letter country code (e.g., 'CA', 'US')
}

function generateSessionToken(): string {
  const bytes = new Uint8Array(18)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 36)
}

export const useGooglePlacesAutocomplete = (options: UseGooglePlacesAutocompleteOptions = {}) => {
  const { onPlaceSelected, country } = options
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const sessionTokenRef = useRef<string | null>(null)
  const previousCountryRef = useRef<string | undefined>(country)

  // Reset session token and clear suggestions when country changes
  useEffect(() => {
    if (previousCountryRef.current !== country) {
      sessionTokenRef.current = null
      setSuggestions([])
      previousCountryRef.current = country
    }
  }, [country])

  /** Search for address suggestions via backend proxy (Places API New autocomplete). */
  const onSearch = useCallback(
    async (input: string) => {
      if (!input.trim()) {
        setSuggestions([])
        setLoading(false)
        return
      }

      setLoading(true)

      try {
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = generateSessionToken()
        }

        const body: Record<string, unknown> = {
          input: input.trim(),
          sessionToken: sessionTokenRef.current,
          includedPrimaryTypes: ['street_address', 'premise'],
        }
        if (country && country.length === 2) {
          body.includedRegionCodes = [country.toUpperCase()]
        }

        const response = await apiClient<{ suggestions?: AutocompleteSuggestion[]; error?: { message: string } }>(
          '/google/places/autocomplete',
          { method: 'POST', body: JSON.stringify(body) },
        )

        if (response?.error) {
          console.error('Places autocomplete error:', response.error.message)
          setSuggestions([])
          return
        }

        if (response?.suggestions && Array.isArray(response.suggestions)) {
          const validSuggestions = response.suggestions.filter((suggestion: any) => {
            const hasPlacePrediction = suggestion?.placePrediction
            const hasPlaceId = suggestion?.placePrediction?.placeId
            const hasText = suggestion?.placePrediction?.text || suggestion?.placePrediction?.structuredFormat
            return !!(hasPlacePrediction && hasPlaceId && hasText)
          })
          setSuggestions(validSuggestions)
        } else {
          setSuggestions([])
        }
      } catch (error) {
        console.error('Error fetching autocomplete suggestions:', error)
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    },
    [country],
  )

  /**
   * Parse address components from Google Places API
   * Works with both old and new Place API structures
   */
  const parseAddressComponents = (addressComponents: any[]): ParsedAddress => {
    const parsed: ParsedAddress = {}

    if (!addressComponents || addressComponents.length === 0) {
      return parsed
    }

    // Helper to get component value - handles Places API (New) longText/shortText and legacy formats
    const getComponentValue = (component: any, useShort: boolean = false): string | undefined => {
      if (!component) return undefined

      const raw = useShort
        ? component.shortName ?? component.short_name ?? component.shortText ?? component.text
        : component.longName ?? component.long_name ?? component.longText ?? component.text

      if (typeof raw === 'string') return raw
      if (raw && typeof raw === 'object' && typeof raw.text === 'string') return raw.text

      if (typeof component === 'string') return component
      return component.value ?? component.name
    }

    // Helper to find component by types
    const findComponent = (types: string[], useShort: boolean = false) => {
      const component = addressComponents.find((ac) => {
        if (!ac) return false
        
        // Handle both array format and object format for types
        let componentTypes: string[] = []
        
        if (Array.isArray(ac.types)) {
          componentTypes = ac.types
        } else if (ac.types) {
          componentTypes = [ac.types]
        } else if (ac.type) {
          // Some APIs use 'type' instead of 'types'
          componentTypes = Array.isArray(ac.type) ? ac.type : [ac.type]
        }
        
        // Check if any of the requested types match
        return types.some((type) => componentTypes.includes(type))
      })
      
      if (component) {
        const value = getComponentValue(component, useShort)
        if (value) {
          console.log(`Found ${types[0]}:`, value, 'from component:', component)
        }
        return value
      } else {
        console.log(`Component not found for types:`, types)
      }
      
      return undefined
    }

    // Extract street number and route for address
    const streetNumber = findComponent(['street_number']) || ''
    const route = findComponent(['route']) || ''
    if (streetNumber || route) {
      parsed.address = `${streetNumber} ${route}`.trim()
    }

    // Extract city - prioritize more specific locations first, but prefer locality over neighborhood
    // For addresses like "7 Queen Elizabeth Boulevard, Etobicoke, ON, Canada"
    // Etobicoke is typically a sublocality, while Toronto is the locality
    // We want to use the more specific one (sublocality) when available
    // However, for addresses like "800 Macleod Trail, Downtown, Calgary, AB"
    // We want "Calgary" (locality) not "Downtown" (neighborhood)
    const city =
      findComponent(['sublocality_level_1']) || // Most specific
      findComponent(['sublocality']) || // More specific than locality
      findComponent(['locality']) || // Actual city name (prefer over neighborhood)
      findComponent(['neighborhood']) || // District within city (fallback)
      findComponent(['postal_town'])

    if (city) {
      parsed.city = city
    }

    // Extract province/state (administrative_area_level_1) - use short name
    const province = findComponent(['administrative_area_level_1'], true)
    if (province) {
      parsed.province = province
    }

    // Extract postal code
    const postalCode = findComponent(['postal_code'])
    if (postalCode) {
      parsed.postalCode = postalCode
    }

    // Extract country - use short name (2-letter code)
    const countryCode = findComponent(['country'], true)
    if (countryCode) {
      parsed.country = countryCode
    }

    return parsed
  }

  /**
   * Fetch place details via backend proxy (GET .../google/places/details).
   */
  const fetchPlaceDetails = useCallback(
    async (placeId: string): Promise<ParsedAddress | null> => {
      try {
        const fields = 'addressComponents,location,formattedAddress,id,displayName'
        const response = await apiClient<{
          addressComponents?: any[]
          location?: { latitude: number; longitude: number }
          formattedAddress?: string
          error?: { message: string }
        }>(`/google/places/details?place_id=${encodeURIComponent(placeId)}&fields=${encodeURIComponent(fields)}`, {})

        if (response?.error) {
          console.error('Place details error:', response.error.message)
          return null
        }

        let addressComponents = response?.addressComponents ?? []
        if (!Array.isArray(addressComponents)) {
          addressComponents = []
        }

        const parsed = parseAddressComponents(addressComponents)

        const lat = response?.location?.latitude
        const lng = response?.location?.longitude
        if (lat != null && lng != null) {
          parsed.latitude = lat
          parsed.longitude = lng
        }

        sessionTokenRef.current = null

        if (onPlaceSelected) {
          onPlaceSelected(parsed)
        }

        return parsed
      } catch (error) {
        console.error('Error fetching place details:', error)
        return null
      }
    },
    [onPlaceSelected],
  )

  return {
    suggestions,
    loading,
    onSearch,
    fetchPlaceDetails,
  }
}

